import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client';
import { useJoinInfo, useJoinWithCurrentAccount, useJoinWithNewAccount } from '../api/members';
import type { JoinInfo, Me } from '../api/types';
import { setActiveChoir, useLogin, useLogout, useMe } from '../auth/session';
import { credentialsFrom } from '../auth/credentials';
import { signInErrorText } from '../auth/signInError';
import { PrivacyNotice } from '../components/PrivacyNotice';
import { applyLanguage, LANGUAGES } from '../i18n';
import ui from '../styles/ui.module.css';
import styles from './JoinPage.module.css';
import { Brand } from '../components/Logo';
import { PasswordInput } from '../components/PasswordInput';

/** At least this many characters, as the server requires. */
const MIN_PASSWORD = 12;

function apiErrorText(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : undefined;
  return t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') });
}

/**
 * Joining a choir from an invitation link (wireframe 15): someone new sets a password and reads the
 * privacy notice; someone with an account signs in and the choir is added to it. Either way they
 * join with the invitation's part and roles and land on their home page.
 */
export function JoinPage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const info = useJoinInfo(token);
  const me = useMe();

  let body;
  if (info.isPending || me.isPending) {
    body = <p className={ui.muted}>{t('common.loading')}</p>;
  } else if (info.isError) {
    const notFound = info.error instanceof ApiError && info.error.status === 404;
    body = <Closed text={notFound ? t('join.statuses.notFound') : t('common.error')} />;
  } else if (info.data.status !== 'PENDING') {
    body = <Closed text={t(`join.statuses.${info.data.status}`)} />;
  } else if (me.data) {
    body = <SignedIn token={token} info={info.data} me={me.data} />;
  } else {
    body = <SignedOut token={token} info={info.data} />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Brand size={36} className={styles.brand} />
        <div className={styles.languages} role="group" aria-label={t('profile.language')}>
          {LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              lang={language}
              aria-pressed={i18n.language === language}
              className={styles.languageButton}
              onClick={() => applyLanguage(language)}
            >
              {t(`languages.${language}`)}
            </button>
          ))}
        </div>
      </header>
      <main className={styles.main}>
        <div className={`${ui.card} ${styles.card}`} data-testid="join">
          {info.data && info.data.status === 'PENDING' && <Intro info={info.data} />}
          {body}
        </div>
      </main>
    </div>
  );
}

function Intro({ info }: { info: JoinInfo }) {
  const { t } = useTranslation();
  const values = {
    inviter: info.invitedBy,
    role: info.sectionLeader ? t('join.roleSectionLeader') : t('join.roleChorist'),
    part: t(`parts.${info.part}`),
  };
  return (
    <>
      <div className={styles.eyebrow}>{t('join.eyebrow')}</div>
      <h1 className={styles.title}>{t('join.title', { choir: info.choirName })}</h1>
      <p className={styles.lead}>{info.invitedBy ? t('join.lead', values) : t('join.leadNoInviter', values)}</p>
    </>
  );
}

function Closed({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.closed} role="alert">
        {text}
      </p>
      <Link to="/">{t('join.toApp')}</Link>
    </>
  );
}

/** The privacy notice with its tick box. */
function Notice({ read, onRead }: { read: boolean; onRead: (read: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <h2 className={styles.sectionTitle}>{t('join.yourData')}</h2>
      <PrivacyNotice />
      <label className={styles.check}>
        <input type="checkbox" checked={read} onChange={(e) => onRead(e.target.checked)} />
        {t('join.read')}
      </label>
    </>
  );
}

/** After joining: work in the new choir, from the home page. */
function useJoined(choirId: string) {
  const navigate = useNavigate();
  return () => {
    setActiveChoir(choirId);
    navigate('/', { replace: true });
  };
}

function SignedOut({ token, info }: { token: string; info: JoinInfo }) {
  const [mode, setMode] = useState<'new' | 'signIn'>(info.accountExists ? 'signIn' : 'new');
  return mode === 'new' ? (
    <NewAccountForm token={token} info={info} onSignIn={() => setMode('signIn')} />
  ) : (
    <SignInForm info={info} onNewAccount={() => setMode('new')} />
  );
}

function NewAccountForm({ token, info, onSignIn }: { token: string; info: JoinInfo; onSignIn: () => void }) {
  const { t, i18n } = useTranslation();
  const join = useJoinWithNewAccount(token);
  const joined = useJoined(info.choirId);
  const [name, setName] = useState(info.displayName);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [read, setRead] = useState(false);
  const mismatch = again.length > 0 && password !== again;
  const ready = name.trim() && email.trim() && password.length >= MIN_PASSWORD && password === again && read;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    join.mutate(
      { displayName: name.trim(), email: email.trim(), password, privacyNoticeRead: read, locale: i18n.language },
      { onSuccess: joined },
    );
  }

  return (
    <form onSubmit={submit} noValidate className={styles.form}>
      <label className={ui.field}>
        <span className={ui.label}>{t('join.name')}</span>
        <input className={ui.input} value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} />
      </label>
      <label className={ui.field}>
        <span className={ui.label}>{t('join.email')}</span>
        <input className={ui.input} type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
      </label>
      <div className={styles.passwords}>
        <div className={ui.field}>
          <label htmlFor="join-password" className={ui.label}>
            {t('join.password')}
          </label>
          <PasswordInput id="join-password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className={ui.field}>
          <label htmlFor="join-password-again" className={ui.label}>
            {t('join.passwordAgain')}
          </label>
          <PasswordInput id="join-password-again" value={again} autoComplete="new-password" onChange={(e) => setAgain(e.target.value)} />
        </div>
      </div>
      <p className={styles.hint}>
        {t('join.passwordHint')} {t('join.haveAccount')}{' '}
        <button type="button" className={ui.linkButton} onClick={onSignIn}>
          {t('join.signIn')}
        </button>{' '}
        {t('join.haveAccountEnd')}
      </p>
      {mismatch && (
        <p className={styles.error} role="alert">
          {t('join.mismatch')}
        </p>
      )}
      <Notice read={read} onRead={setRead} />
      {join.isError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {apiErrorText(join.error, t)}
        </div>
      )}
      <div className={styles.actions}>
        <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!ready || join.isPending}>
          {join.isPending ? t('join.submitting') : t('join.submit')}
        </button>
      </div>
    </form>
  );
}

/** Someone with an account signs in here; the page then offers to add the choir. */
function SignInForm({ info, onNewAccount }: { info: JoinInfo; onNewAccount: () => void }) {
  const { t } = useTranslation();
  const login = useLogin();
  const [missing, setMissing] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    // Read from the fields: the browser may have filled in a saved password (auth/credentials.ts).
    const credentials = credentialsFrom(event.currentTarget as HTMLFormElement);
    setMissing(!credentials);
    if (credentials) login.mutate(credentials);
  }

  return (
    <form onSubmit={submit} noValidate className={styles.form}>
      {info.accountExists && <p className={ui.muted}>{t('join.accountExists')}</p>}
      <h2 className={styles.sectionTitle}>{t('join.signInTitle', { choir: info.choirName })}</h2>
      <label className={ui.field}>
        <span className={ui.label}>{t('login.email')}</span>
        <input className={ui.input} type="email" name="email" autoComplete="username" />
      </label>
      <div className={ui.field}>
        <label htmlFor="join-signin-password" className={ui.label}>
          {t('login.password')}
        </label>
        <PasswordInput id="join-signin-password" name="password" autoComplete="current-password" />
      </div>
      {(missing || login.isError) && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {missing ? t('login.missing') : signInErrorText(login.error, t)}
        </div>
      )}
      <div className={styles.actions}>
        <button type="button" className={ui.linkButton} onClick={onNewAccount}>
          {t('join.newAccountInstead')}
        </button>
        <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={login.isPending}>
          {t('join.signInSubmit')}
        </button>
      </div>
    </form>
  );
}

function SignedIn({ token, info, me }: { token: string; info: JoinInfo; me: Me }) {
  const { t } = useTranslation();
  const join = useJoinWithCurrentAccount(token);
  const logout = useLogout();
  const joined = useJoined(info.choirId);
  const needsNotice = !me.privacyNoticeReadAt;
  const [read, setRead] = useState(false);

  if (me.memberships.some((m) => m.choirId === info.choirId)) {
    return (
      <>
        <p className={styles.closed}>{t('join.alreadyMember', { choir: info.choirName })}</p>
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={joined}>
          {t('join.toApp')}
        </button>
      </>
    );
  }

  return (
    <div className={styles.form}>
      <p>
        {t('join.signedInAs', { name: me.displayName, email: me.email })}{' '}
        <button type="button" className={ui.linkButton} onClick={() => logout.mutate()}>
          {t('join.notYou')}
        </button>
      </p>
      {needsNotice && <Notice read={read} onRead={setRead} />}
      {join.isError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {apiErrorText(join.error, t)}
        </div>
      )}
      <div className={styles.actions}>
        <button
          type="button"
          className={`${ui.button} ${ui.primary}`}
          disabled={(needsNotice && !read) || join.isPending}
          onClick={() => join.mutate(read, { onSuccess: joined })}
        >
          {join.isPending ? t('join.submitting') : t('join.addToAccount')}
        </button>
      </div>
    </div>
  );
}
